import { waitUntil, consoleEvents, getCookie, setCookie } from '@pb/utils'
import { createInstance, enums, setLogLevel } from '@optimizely/react-sdk'

const optimizelyInstance: ReturnType<typeof createInstance> = createInstance({
  sdkKey: process.env.OPTIMIZELY_SDK_KEY,
})

const optimizelyFullStackOptOut = getCookie('ab_optout')

const userSetDebugging = localStorage.getItem('optimizelyDebuggerLevel')
if (userSetDebugging) {
  setLogLevel(userSetDebugging)
} else {
  setLogLevel(process.env.OPTIMIZELY_LOG_LEVEL ?? 'unset')
}

// Contentsquare x Optimizely Full Stack integration
const getIsContentSquareConfigAvailable = (): any => {
  return window.CS_CONF && window._uxa
}
const onActivate = async (decisionObject): Promise<any> => {
  let disableCallback = false

  const callback = async (): Promise<any> => {
    if (!disableCallback) {
      disableCallback = true
      try {
        window.CS_CONF.integrations = window.CS_CONF.integrations || []
        window.CS_CONF.integrations.push('Optimizely Full Stack')
      } catch {
        consoleEvents.info('window.CS_CONF is not available')
      }
    }
  }

  try {
    await waitUntil(getIsContentSquareConfigAvailable)
    window._uxa.push(['afterPageView', callback])

    const csPrefix = 'AB_OP_'
    const csKey = csPrefix + decisionObject.experiment.key
    const csValue = decisionObject.variation.key
    window._uxa = window._uxa || []
    window._uxa.push(['trackDynamicVariable', { key: csKey, value: csValue }])
  } catch {
    consoleEvents.info('window._uxa is not available')
  }
}

// GA x Optimizely Full Stack integration for OFS experiments (only experiments)
const onDecision = async (decision): Promise<any> => {
  const { decisionInfo, type } = decision

  if (
    type === 'flag' &&
    window.abTest?.trackExperiment &&
    decisionInfo?.decisionEventDispatched &&
    decisionInfo?.flagKey &&
    decisionInfo?.variationKey
  ) {
    window.abTest.trackExperiment(
      `${decisionInfo.flagKey}|${decisionInfo.ruleKey}`,
      decisionInfo.variationKey,
    )
  }
}

optimizelyInstance.notificationCenter.addNotificationListener(
  enums.NOTIFICATION_TYPES.ACTIVATE,
  onActivate,
)

optimizelyInstance.notificationCenter.addNotificationListener(
  enums.NOTIFICATION_TYPES.DECISION,
  onDecision,
)

optimizelyInstance.onUserUpdate((user) => {
  localStorage.setItem('optimizelyUserAttributes', JSON.stringify(user.attributes))
})

export const trackOptimizelyEvent = (
  eventKey: string,
  overrideUserId?: string,
  attributes?: {},
  eventTags?: {},
): void => {
  try {
    const overrideAttributes = { ...optimizelyInstance.user?.attributes, ...attributes }
    localStorage.setItem('optimizelyUserAttributes', JSON.stringify(overrideAttributes))
    if (optimizelyFullStackOptOut === null) {
      optimizelyInstance.track(eventKey, overrideUserId, overrideAttributes, eventTags)
    }
  } catch (error) {
    consoleEvents.error(error)
  }
}

// Splunk integration / must done on an individual experiment level / works for both toggles & experiments
export const monitorTestWithSplunk = (key: string, variation?: string | boolean | null): void => {
  if (variation === null || variation === 'unbucketed') return

  let splunkIds = getCookie('ab_id')
  let separator = '|'
  let parsedVariation = variation
  if (typeof variation === 'boolean') {
    parsedVariation = variation ? 'on' : 'off'
  }

  const shorthandInfo = `${key}_${parsedVariation}`

  if (splunkIds === null) {
    splunkIds = ''
    separator = ''
  }

  if (!splunkIds?.includes(shorthandInfo)) {
    splunkIds += `${separator}${shorthandInfo}`
    setCookie('ab_id', splunkIds)
  }
}

export default optimizelyInstance
